import React, { useState, useMemo, useEffect } from "react";
import { Grid, Typography, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { MyPageButton, MyPagePaper, MyPagePanel } from "../../common";
import { BackofficeApplicationResource, InvoicesResource } from "../../../resources";
import { MonthEnum, InvoiceStatusEnum, PPMT, PMT, InterestAmountForPeriod } from "../../../utils";
import { FormattedMessage, useIntl } from "react-intl";
import { useCommonWidths } from "../../../hooks";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const useStyles = makeStyles((theme: Theme) => ({
	paperWrapperMarginTop: {
		marginTop: theme.spacing(2),
	},
	invoicePaymentInfoRow: {
		padding: theme.spacing(2),
		[theme.breakpoints.up("md")]: {
			padding: theme.spacing(3),
		},
	},
	headings: {
		fontWeight: "bold",
	},
	label: {
		fontWeight: "bold",
		color: "grey",
	},
	value: {
		fontWeight: "bold",
	},
	invoiceIsPaidPaperBackground: {
		backgroundColor: "rgba(0,0,0,0.1)",
	},
	invoiceIsPaidTextStyle: {
		fontWeight: 400,
		color: "grey",
	},
	infoDesc: {
		margin: "8px 4px"
	},
	helpIcon: {
		marginLeft: "8px",
		marginTop: "5px",
	}
}));

interface PaymentPlanTabPageProps {
	backofficeApplication: BackofficeApplicationResource;
	interestRateChanges?: InvoicesResource[];
}

interface InvoicePaymentInfoRowProps {
	backofficeApplication?: BackofficeApplicationResource;
	paymentPlan?: PaymentPlan;
	className?: string;
	restBalance?: number;
}

interface PaymentPlan {
	dueDate: Date;
	interestAmount: number;
	installmentAmount: number;
	principalAmount: number;
	restbalance: number;
	status: InvoiceStatusEnum;
	termFee: number;
}

const calculatePaymentPlan = (
	backofficeApplication: BackofficeApplicationResource,
	interestRateChanges?: InvoicesResource[]
) => {
	const {
		interestRate,
		termLength,
		remainingTerms,
		loanBalance,
	} = backofficeApplication;

	let decrementingLoanBalance = loanBalance;

	const futurePaymentPlan = () => {
		const _paymentPlan = [] as PaymentPlan[];

		let dueDate = new Date(
			backofficeApplication.nextDueDate
		);
		dueDate.setDate(1); //	Safehandling for eg. 30. feb

		let interestRateToUse = interestRate;

		let termFee = backofficeApplication.termFee;
		let installmentAmount = backofficeApplication.installmentAmount;

		let deductFree = backofficeApplication.restDeductionFree;
		let calculatingAfterDeductionFreeTerms = false;

		let remTermsCounter = 0;

		let calculateNewInterestRateThisTerm = false;
		let paymentPlanHasCalculatedAnInterestRateChange = false;
		let interestRateChangesThisTerm: InvoicesResource[] = [];

		while (remTermsCounter < remainingTerms) {

			//	Initiate this term's interestrate-change-invoices if they should take effect this term:
			if(interestRateChanges){
				for(let i = 0; i < interestRateChanges.length; i++){
					if(dueDate.getMonth() === new Date(interestRateChanges[i].interestRateFromDate).getMonth()){
						if(dueDate.getFullYear() === new Date(interestRateChanges[i].interestRateFromDate).getFullYear()){
							//	If the interestrate change is registered this month/ term, but should affect the NEXT TERM instead of this term:
							if (!interestRateChanges[i].interestRateChangeTakesEffectNextTerm){
								interestRateChangesThisTerm.push(interestRateChanges[i]);
								calculateNewInterestRateThisTerm = true;
							}
						}
					}
					//	This iteration will check the term/ duedate previous to this one.
					//	If the previous term's interestrate change should affect THIS TERM:
					else if(interestRateChanges[i].interestRateChangeTakesEffectNextTerm){
						let dueDateCopy = new Date(dueDate);
						dueDateCopy.setMonth(dueDateCopy.getMonth() - termLength);

						if(dueDateCopy.getMonth() === new Date(interestRateChanges[i].interestRateFromDate).getMonth()){
							if(dueDateCopy.getFullYear() === new Date(interestRateChanges[i].interestRateFromDate).getFullYear()){
								interestRateChangesThisTerm.push(interestRateChanges[i]);
								calculateNewInterestRateThisTerm = true;
							}
						}
					}
				}
			}

			//	Reset interestamount, and recalculate the principalAmount for each term:
			let interestAmount = 0;
			let calculatedPrincipalAmount = Math.abs(
				Math.round(
					PPMT(
						interestRateToUse / (12 / termLength),
						termLength,
						remainingTerms - remTermsCounter,
						decrementingLoanBalance,
						0,
						0
					)
				)
			);

			//	All terms after deduction free terms. Reset principal and installment amounts
			if (calculatingAfterDeductionFreeTerms) {
				installmentAmount =
					Math.abs(
						Math.round(
							PMT(
								interestRateToUse / (12 / termLength),
								remainingTerms - remTermsCounter,
								decrementingLoanBalance
							) // PMT() returns InstallmentAmount (PrincipalAmount + InterestAmount)
						)
					) + termFee;
				interestAmount =
					installmentAmount - termFee - calculatedPrincipalAmount;
				decrementingLoanBalance -= calculatedPrincipalAmount;
			}
			//	Term is deduction free. Only calculate interests. (And there isn't a change in interestrate)
			else if (deductFree > 0 && !calculateNewInterestRateThisTerm) {
				interestAmount = InterestAmountForPeriod(decrementingLoanBalance, interestRateToUse, 30, termLength);
				installmentAmount = interestAmount + termFee;
				deductFree--;

				calculatedPrincipalAmount = 0;

				calculatingAfterDeductionFreeTerms = deductFree === 0;
			}
			//	Calculate interestrate changes for this one term:
			else if(calculateNewInterestRateThisTerm && interestRateChanges){
				var termDaysCounterWithInterestRates_Max30 = 0;
				var accumulatedInterestAmounts = 0;
				var previousInvoiceDaysWithOldInterestRate = 0;

				for(let i = 0; i < interestRateChangesThisTerm.length; i++){
					var daysWithThisInterestRate = interestRateChangesThisTerm[i].daysWithOldInterestRate;

					// If there are more than 1 interestrate changes during this one term:
					if(i > 0){
						//	All interestRateChanges' "daysWithOldInterestRate" is calculated from last term-invoice's startdate. Subtract the difference
						/* Eg. We have 2 interestrate-change-invoices, one from 05.01.2021 [A], and one from 15.01.2021 [B], and an ordinary terminvoice from 01.01.2021 [C]
						 * [A] has 4 days (5 - 1) with old interest rate. [B] has 14 days (15 - 1) with old interest rate.
						 * [B] should only calculate 10 days with the old interest rate, instead of the 14 days because [A] has already covered those 4 days */
						daysWithThisInterestRate -= previousInvoiceDaysWithOldInterestRate;
					}
					termDaysCounterWithInterestRates_Max30 += daysWithThisInterestRate;

					accumulatedInterestAmounts += InterestAmountForPeriod(decrementingLoanBalance, interestRateToUse, daysWithThisInterestRate, termLength)
					interestRateToUse = interestRateChanges[i].interestRate;
					previousInvoiceDaysWithOldInterestRate = daysWithThisInterestRate;
				}

				//	Calculate interestamount with the new interestrate for the next 30 - X days of the term:
				var sumInterestsWithNewInterestRate = InterestAmountForPeriod(
					decrementingLoanBalance, interestRateToUse, 30 - termDaysCounterWithInterestRates_Max30, termLength
				);
				interestAmount = accumulatedInterestAmounts + sumInterestsWithNewInterestRate;

				//	If this term in addition is deduction free:
				if(deductFree > 0){
					installmentAmount = interestAmount + termFee;
					deductFree--;
					calculatedPrincipalAmount = 0;
					calculatingAfterDeductionFreeTerms = deductFree === 0;
				}
				else {
					installmentAmount = calculatedPrincipalAmount + termFee + interestAmount;
					//	Recalculate principalAmount after changed interestrate:
					calculatedPrincipalAmount = Math.round(Math.abs(
						PPMT(
							interestRateToUse / (12 / termLength),
							termLength,
							remainingTerms - remTermsCounter,
							decrementingLoanBalance,
							0,
							0
						)
					));
					decrementingLoanBalance -= calculatedPrincipalAmount;
				}

				paymentPlanHasCalculatedAnInterestRateChange = true;

				//	Clear array for eventually new interestrate changes another term:
				interestRateChangesThisTerm = [];
			}
			else {
				//	Calculation of future paymentplan after handling terms with interestrate changes:
				if(paymentPlanHasCalculatedAnInterestRateChange){
					interestAmount = InterestAmountForPeriod(
						decrementingLoanBalance, interestRateToUse, 30, termLength
					);
					installmentAmount = termFee + interestAmount + calculatedPrincipalAmount;
					decrementingLoanBalance -= calculatedPrincipalAmount;
				}
				//	Ordinary paymentplan calculation, without deductionfree terms or terms with interestrate changes:
				else {
					installmentAmount = backofficeApplication.installmentAmount;
					interestAmount = installmentAmount - calculatedPrincipalAmount - termFee;
					decrementingLoanBalance -= calculatedPrincipalAmount;
				}
			}

			_paymentPlan.push({
				dueDate: new Date(dueDate),
				interestAmount: interestAmount,
				installmentAmount: installmentAmount,
				principalAmount: calculatedPrincipalAmount,
				restbalance: decrementingLoanBalance,
				status: InvoiceStatusEnum.ToPay,
				termFee: backofficeApplication.termFee,
			} as PaymentPlan);

			//	reset term-state-variables for next iteration
			remTermsCounter++;
			calculateNewInterestRateThisTerm = false;

			dueDate.setMonth(dueDate.getMonth() + termLength);
			let newDate = new Date(dueDate);
			dueDate = new Date(newDate);
		}
		return _paymentPlan.reverse();
	};
	return futurePaymentPlan();
};

const PaymentPlanTabPage = (props: PaymentPlanTabPageProps) => {
	const { backofficeApplication, interestRateChanges } = props;
	const classes = useStyles();

	const isMobile = useCommonWidths().isSmDown;

	const [paymentPlan, setPaymentPlan] = useState([] as PaymentPlan[]);
	useEffect(() => {
		setPaymentPlan(calculatePaymentPlan(backofficeApplication, interestRateChanges));
	}, [backofficeApplication, interestRateChanges]);

	const [
		currentlyDisplayedInvoicesByYearFilter,
		setCurrentlyDisplayedInvoicesByYearFilter,
	] = useState(paymentPlan);

	const yearRange = useMemo(() => {
		let _yearRange = [];
		for (var i = 0; i < paymentPlan.length; i++) {
			if (paymentPlan[i].dueDate) {
				var d = new Date(paymentPlan[i].dueDate).getFullYear();
				_yearRange.push(d);
			}
		}
		return _yearRange.filter(getUniqueYears);
	}, [paymentPlan]);

	const earliestYear = yearRange[yearRange.length - 1];
	const latestYear = yearRange[0];
	const currentYear = new Date().getFullYear();
	const [currentYearFilter, setCurrentYearFilter] = useState(
		backofficeApplication.fulfilled ? latestYear : currentYear
	);

	const [displayInfoText, setDisplayInfoText] = useState(false);

	useEffect(() => {
		const initialYearFilterToUse = backofficeApplication.fulfilled
			? latestYear
			: currentYear;
		setCurrentYearFilter(initialYearFilterToUse);

		setCurrentlyDisplayedInvoicesByYearFilter(
			paymentPlan.filter((i) => {
				const invoiceYear = i.dueDate.getFullYear();
				return invoiceYear === initialYearFilterToUse;
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentPlan.length, backofficeApplication]);

	const changeYearFilter = (newYearFilter: number) => {
		setCurrentYearFilter(newYearFilter);

		setCurrentlyDisplayedInvoicesByYearFilter(
			paymentPlan.filter((i) => {
				const invoiceYear = i.dueDate.getFullYear();
				return invoiceYear === newYearFilter;
			})
		);
	};

	return (
		<MyPagePanel>
			<Grid container direction="column" spacing={isMobile ? 4 : 6}>
				<Grid item container direction="row">
					<Grid item>
						<Typography
							variant={isMobile ? "h6" : "h5"}
							className={classes.headings}
							>
							<FormattedMessage
								id="minside.backofficeApplications.paymentplan"
								defaultMessage="Betalingsplan"
								description="Paymentplan label"
								/>
						</Typography>
					</Grid>
					{isMobile && (
					<Grid>
						<InfoOutlinedIcon className={classes.helpIcon} onClick={() => setDisplayInfoText(!displayInfoText)} fontSize="small" />
					</Grid>
					)}
					{(displayInfoText || !isMobile) && (
						<Typography
							variant={isMobile ? "subtitle2" : "body1"}
							className={classes.infoDesc}
							>
							<FormattedMessage
								id="minside.backofficeApplications.paymentplan_description"
								defaultMessage="Betalingsplanen viser en oversikt over fremtidige forfall på  lånet ditt uten purregebyrer, forsinkelsesrenter og eventuelle tilleggsprodukter som f.eks. betalingsforsikring. Vi gjør oppmerksom på at dette er poster som kan komme i tillegg på fakturaen din.
								Oversikt over tidligere utfakturerte terminer finner du under faktura-fanen."
								description="Paymentplan info description"
								/>
						</Typography>
					)}
				</Grid>
				<Grid item>
					<MyPagePaper>
						<InvoicePaymentInfoRow
							className={classes.invoicePaymentInfoRow}
							backofficeApplication={backofficeApplication}
							/>
					</MyPagePaper>
				</Grid>

				<Grid item>
					{isMobile && (
						<Typography variant="subtitle2" gutterBottom>
							<FormattedMessage
								id="minside.paymentPlan.termFee_not_showing_for_mobile"
								defaultMessage="Termingebyr vises ikke i oversikten nedenfor"
								description="TermFee isn't displayed in the paymentplan section below"
							/>
						</Typography>
					)}
					<Grid item xs={isMobile ? 7 : 3}>
						<MyPagePaper style={{ padding: 0 }}>
							<Grid container direction="row" justify="space-between">
								<Grid item>
									<MyPageButton
										backIcon
										noFlex
										onClick={() => {
											changeYearFilter(currentYearFilter - 1);
										}}
										disabled={currentYearFilter <= earliestYear}
									/>
								</Grid>
								<Grid item style={{ alignSelf: "center" }}>
									<Typography className={classes.value}>
										{currentYearFilter}
									</Typography>
								</Grid>
								<Grid item>
									<MyPageButton
										nextIcon
										noFlex
										onClick={() => {
											changeYearFilter(currentYearFilter + 1);
										}}
										disabled={currentYearFilter >= latestYear}
									/>
								</Grid>
							</Grid>
						</MyPagePaper>
					</Grid>
					{currentlyDisplayedInvoicesByYearFilter.map((invoice, idx) => {
						return (
							<MyPagePaper
								className={classes.paperWrapperMarginTop}
								key={idx}
							>
								<InvoicePaymentInfoRow paymentPlan={invoice} />
							</MyPagePaper>
						);
					})}
				</Grid>
			</Grid>
		</MyPagePanel>
	);
};

const InvoicePaymentInfoRow = (props: InvoicePaymentInfoRowProps) => {
	const { backofficeApplication, paymentPlan, className } = props;
	const classes = useStyles();

	const isMobile = useCommonWidths().isSmDown;
	const intl = useIntl();

	const termIsComplete = paymentPlan && (
		paymentPlan.status === InvoiceStatusEnum.Paid ||
		paymentPlan.status === InvoiceStatusEnum.AddedAmount
	)

	return (
		<Grid
			className={className}
			container
			direction="row"
			alignItems="flex-end"
			justify={isMobile ? "space-between" : "flex-start"}
		>
			{backofficeApplication && (
				<>
					<Grid item xs={isMobile ? "auto" : 2}>
						<Typography variant="caption" className={classes.label}>
							<FormattedMessage
								id="minside.backofficeApplications.paymentplan.loanBalance"
								defaultMessage="Saldo"
								description="Remaining loan balance for this BackofficeApplication"
							/>
						</Typography>
						<Typography
							variant={isMobile ? "body1" : "h5"}
							className={classes.value}
						>
							<FormattedMessage
								id="minside.backofficeApplications.paymentplan.loanBalance.value"
								defaultMessage="{loanBalance}"
								description="Value of remaining loan balance for this BackofficeApplication"
								values={{
									loanBalance: Intl.NumberFormat(intl.locale, {
										style: "currency",
										currency: "NOK",
										maximumFractionDigits: 0,
										minimumFractionDigits: 0,
									}).format(backofficeApplication.loanBalance ?? 0),
								}}
							/>
						</Typography>
					</Grid>
					<Grid item xs={isMobile ? "auto" : 2}>
						<Typography variant="caption" className={classes.label}>
							<FormattedMessage
								id="minside.backofficeApplications.paymentplan.billedNotPayed"
								defaultMessage="Ubetalte fakturaer"
								description="Billed amount that is not payed for this BackofficeApplication"
							/>
						</Typography>
						<Typography
							variant={isMobile ? "body1" : "h5"}
							className={classes.value}
						>
							<FormattedMessage
								id="minside.backofficeApplications.paymentplan.billedNotPayed.value"
								defaultMessage="{billedNotPayed}"
								description="Value of billed amount that is not payed for this BackofficeApplication"
								values={{
									billedNotPayed: Intl.NumberFormat(intl.locale, {
										style: "currency",
										currency: "NOK",
										maximumFractionDigits: 0,
										minimumFractionDigits: 0,
									}).format((backofficeApplication.remainingCost - backofficeApplication.loanBalance) || 0),
								}}
							/>
						</Typography>
					</Grid>
				</>
			)}
			<Grid item xs={isMobile ? "auto" : 2}>
				<Typography variant="caption" className={classes.label}>
					{paymentPlan ? (
						<FormattedMessage
							id="minside.backofficeApplications.paymentplan.remaining_loan"
							defaultMessage="{month} - {year}"
							values={{
								month: isMobile
									? MonthEnum[paymentPlan.dueDate.getMonth()].substring(0, 3)
									: MonthEnum[paymentPlan.dueDate.getMonth()],
								year: paymentPlan.dueDate.getFullYear(),
							}}
						/>
					) : (
						<FormattedMessage
							id="minside.backofficeApplications.paymentplan.remaining_loan.default"
							defaultMessage="Total gjeld"
						/>
					)}
				</Typography>
				<Typography
					variant={isMobile ? "body1" : "h5"}
					className={
						termIsComplete
							? classes.invoiceIsPaidTextStyle
							: classes.value
					}
				>
					<FormattedMessage
						id="minside.backofficeApplications.paymentplan.installmentAmount_or_remainingCost"
						defaultMessage="{amount}"
						values={{
							amount: Intl.NumberFormat(intl.locale, {
								style: "currency",
								currency: "NOK",
								maximumFractionDigits: 0,
								minimumFractionDigits: 0,
							}).format(
								paymentPlan
									? paymentPlan.installmentAmount
									: backofficeApplication?.remainingCost || 0
							),
						}}
					/>
				</Typography>
			</Grid>
			<Grid item xs={isMobile ? "auto" : 2}>
				<Typography variant="caption" className={classes.label}>
					<FormattedMessage
						id="minside.backofficeApplications.paymentplan.termAmountOrInstallmentAmount"
						defaultMessage={paymentPlan ? "Avdrag" : "Terminbeløp"}
						description="InstallmentAmount/Termamount label"
					/>
				</Typography>
				<Typography
					className={
						termIsComplete
							? classes.invoiceIsPaidTextStyle
							: classes.value
					}
				>
					<FormattedMessage
						id="minside.backofficeApplications.paymentplan.principalAmount_or_installmentAmount"
						defaultMessage="{amount}"
						values={{
							amount: Intl.NumberFormat(intl.locale, {
								style: "currency",
								currency: "NOK",
								maximumFractionDigits: 0,
								minimumFractionDigits: 0,
							}).format(
								paymentPlan
									? paymentPlan.principalAmount
									: backofficeApplication?.installmentAmount || 0
							),
						}}
					/>
				</Typography>
			</Grid>
			<Grid item xs={isMobile ? "auto" : 2}>
				<Typography variant="caption" className={classes.label}>
					<FormattedMessage
						id="minside.backofficeApplications.paymentplan.intrest"
						defaultMessage="Rente"
						description="Intrest label"
					/>
				</Typography>
				<Typography
					className={
						termIsComplete
							? classes.invoiceIsPaidTextStyle
							: classes.value
					}
				>
					{paymentPlan && (
						<FormattedMessage
							id="minside.backofficeApplications.paymentplan.interestAmount"
							defaultMessage="{interestAmount}"
							values={{
								interestAmount: Intl.NumberFormat(intl.locale, {
									style: "currency",
									currency: "NOK",
									maximumFractionDigits: 0,
									minimumFractionDigits: 0,
								}).format(paymentPlan?.interestAmount),
							}}
						/>
					)}
					{backofficeApplication && (
						<FormattedMessage
							id="minside.backofficeApplications.backofficeApplication.interestRate"
							defaultMessage="{interestRate} %"
							values={{
								interestRate: Intl.NumberFormat(intl.locale, {
									maximumFractionDigits: 2,
									minimumFractionDigits: 2,
								}).format((backofficeApplication.updatedInterestRateAfterChange ?? backofficeApplication.interestRate) * 100 || 0),
							}}
						/>
					)}
				</Typography>
			</Grid>
			{paymentPlan && !isMobile && (
				<Grid item xs={isMobile ? "auto" : 2}>
					<Typography variant="caption" className={classes.label}>
						<FormattedMessage
							id="minside.backofficeApplications.paymentplan.termFee.label"
							defaultMessage="Termingebyr"
							description="Term fee label"
						/>
					</Typography>
					<Typography
						className={
							termIsComplete
								? classes.invoiceIsPaidTextStyle
								: classes.value
						}
					>
						<FormattedMessage
							id="minside.backofficeApplications.paymentPlan.termFee.value"
							defaultMessage="{termFee}"
							values={{
								termFee: Intl.NumberFormat(intl.locale, {
									style: "currency",
									currency: "NOK",
									maximumFractionDigits: 0,
									minimumFractionDigits: 0,
								}).format(paymentPlan.termFee),
							}}
						/>
					</Typography>
				</Grid>
			)}
			{paymentPlan && (
				<Grid item xs={"auto"}>
					<Typography variant="caption" className={classes.label}>
						<FormattedMessage
							id="minside.backofficeApplications.paymentplan.residual"
							defaultMessage="Restbalanse"
							description="Residual label"
						/>
					</Typography>
					<Typography
						className={
							termIsComplete
								? classes.invoiceIsPaidTextStyle
								: classes.value
						}
					>
						<FormattedMessage
							id="minside.backofficeApplications.paymentPlan.restbalance"
							defaultMessage="{restbalance}"
							values={{
								restbalance: Intl.NumberFormat(intl.locale, {
									style: "currency",
									currency: "NOK",
									maximumFractionDigits: 0,
									minimumFractionDigits: 0,
								}).format(paymentPlan.restbalance),
							}}
						/>
					</Typography>
				</Grid>
			)}
			{backofficeApplication && (
				<Grid item xs={"auto"}>
					<Typography variant="caption" className={classes.label}>
						<FormattedMessage
							id="minside.backofficeApplications.paymentplan.residual"
							defaultMessage="Termingebyr"
							description="Term fee label"
						/>
					</Typography>
					<Typography className={classes.value}>
						<FormattedMessage
							id="minside.backofficeApplications.backofficeApplication.termFee"
							defaultMessage="{termFee}"
							values={{
								termFee: Intl.NumberFormat(intl.locale, {
									style: "currency",
									currency: "NOK",
									maximumFractionDigits: 0,
									minimumFractionDigits: 0,
								}).format(backofficeApplication.termFee),
							}}
						/>
					</Typography>
				</Grid>
			)}
		</Grid>
	);
};

const getUniqueYears = (value: any, index: number, self: any) => {
	return self.indexOf(value) === index;
};

export default PaymentPlanTabPage;
